import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cleanDefId } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { S2GroupedBarChart } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { S2GroupedBarChartGroup } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { S2GroupedBarChartLevel } from 'libs/end-to-end/src/grouped-bar';
import { KeyFactorsAxisMarkersLayer } from 'apps/sigma2-ui/src/components/AxisMarkersLayer';
import { KeyFactorsLabelsLayer } from 'apps/sigma2-ui/src/components/KeyFactorsLabels';
import { KeyFactorsChartBar } from 'apps/sigma2-ui/src/components/KeyFactorsChartBar';
import { AIMagicIconSvg } from 'apps/sigma2-ui/src/components/svg/AIMagicIcon';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { getControlVariableDefinitionsMapping } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { BreakdownSectionSerializableProps } from 'libs/end-to-end/src/success-phenotype';
import { Trait } from 'libs/end-to-end/src/success-phenotype';
import { getBreakdownExplanationForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { getTargetRangeDisplayForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { getExampleExplanationForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { getOptimalRuleExplanationForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { getRuleOutputExplanationForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { traitToTraitLevelDatumsForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { ExpandableExplanation } from 'apps/sigma2-ui/src/components/explanations/ExpandableExplanation';
import { ExplanationsOutputDictionary } from 'libs/end-to-end/src/explanations';
import { TargetRangeGraph } from 'apps/sigma2-ui/src/components/key-factors/TargetRangeGraph';
import { getTargetRangeProps } from 'apps/sigma2-ui/src/components/key-factors/BreakdownSectionUtils';

const REST_COLOR = {
  type: 'linearGradient',
  x1: 1,
  y1: 0,
  x2: 0,
  y2: 1,
  colors: [
    { offset: 0, color: '#DFE6F9' },
    { offset: 100, color: '#F5F6FA' },
  ],
};

const HOVER_COLOR = {
  type: 'linearGradient',
  x1: 1,
  y1: 0,
  x2: 0,
  y2: 1,
  colors: [
    { offset: 0, color: '#C9D5F8' },
    { offset: 100, color: '#F5F6FA' },
  ],
};

const SELECTED_COLOR = {
  type: 'linearGradient',
  x1: 1,
  y1: 0,
  x2: 0,
  y2: 1,
  colors: [
    { offset: 0, color: '#A1B8F7' },
    { offset: 100, color: '#F5F6FA' },
  ],
};

const CustomYAxisLabel = () => (
  <text
    x="0"
    y="-22"
    textAnchor="start"
    dominantBaseline="hanging"
    style={{
      fontSize: 14,
      fontWeight: '700',
      fill: '#52575B',
      fontFamily: 'Karla',
    }}
  >
    Likelihood %
  </text>
);

const Chart = ({
  setSelectedIndex,
  xGroups,
  xLevels,
}: {
  setSelectedIndex: (index: number) => void;
  xGroups: S2GroupedBarChartGroup[];
  xLevels: S2GroupedBarChartLevel[];
}) => {
  const computedDefs = useMemo(
    () =>
      xGroups.flatMap((xGroup) =>
        xGroup.groupLevels.flatMap((xLevel) => [
          {
            id: cleanDefId(`${xLevel.id}${xGroup.id}_Selected`),
            ...SELECTED_COLOR,
          },
          {
            id: cleanDefId(`${xLevel.id}${xGroup.id}`),
            ...REST_COLOR,
          },
          {
            id: cleanDefId(`${xLevel.id}${xGroup.id}_Hover`),
            ...HOVER_COLOR,
          },
        ]),
      ),
    [xGroups],
  );

  /**
   * Computes horizontal group padding.
   * 0.65 for 1 group, 0.3 for 2, and linearly interpolates down to 0.1 for 3 or more.
   */
  const horizontalGroupPadding = useMemo(() => {
    const n = xGroups.length;
    if (n <= 1) return 0.65;
    if (n === 2) return 0.3;
    return 0.1;
  }, [xGroups]);

  return (
    <div className="min-h-80">
      <S2GroupedBarChart
        xGroups={xGroups}
        xLevels={xLevels}
        yAxisLabel=""
        units="percentage"
        horizontalHeightAsFactorOfWidth={0.4}
        margin={{ top: 50, right: 0, bottom: 24, left: 2 }}
        layers={[
          CustomYAxisLabel,
          KeyFactorsAxisMarkersLayer,
          KeyFactorsLabelsLayer,
        ]}
        enableGridX={false}
        enableGridY={false}
        defs={computedDefs}
        markers={[]}
        enableAxisY={false}
        enableAxisX={false}
        theme={{
          axis: {
            domain: {
              line: {
                stroke: '#E7E8E8',
                strokeWidth: '1px',
              },
            },
          },
        }}
        barComponent={KeyFactorsChartBar}
        horizontalGroupPadding={horizontalGroupPadding}
        onBarClick={(barProps) => {
          setSelectedIndex(barProps.bar.data.index);
        }}
      />
    </div>
  );
};

const ChartSection = ({
  trait,
  metricColumnHeaderValue,
  explanations,
  selectedIndex,
  originalLevelIndex,
  navigateToTraitLevel,
}: {
  trait: Trait;
  metricColumnHeaderValue: string;
  explanations?: ExplanationsOutputDictionary;
  selectedIndex: number;
  originalLevelIndex: number | null;
  navigateToTraitLevel: (
    trait: Trait | null,
    levelIndex: number | null,
  ) => void;
}) => {
  const { myObservation } = useObservation();

  const [xGroups, xLevels] = useMemo(() => {
    if (!myObservation) {
      return [[], []];
    }

    const controlVariableDefinitionsMapping =
      getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation({
        myObservation,
      });

    const { datums: traitDatum } = traitToTraitLevelDatumsForKeyFactors({
      controlVariableDefinitionsMapping,
      activeTrait: trait,
      withoutUnit: true,
    });

    const xGroups = traitDatum.map((datum) => ({
      id: datum.id,
      label: datum.id,
      groupLevels: [
        {
          id: trait.controlVariableColumnHeaderValue,
          value: datum.value,
        },
      ],
    }));

    const xLevels = [
      {
        id: trait.controlVariableColumnHeaderValue,
        label: trait.controlVariableColumnHeaderValue,
      },
    ];

    return [xGroups, xLevels];
  }, [trait, myObservation]);

  const metric = useMemo(() => {
    for (const focusArea of myObservation?.focusAreas ?? []) {
      for (const metric of focusArea.metrics) {
        if (metric.columnHeaderValue === metricColumnHeaderValue) {
          return metric;
        }
      }
    }
  }, [metricColumnHeaderValue, myObservation]);

  const chartDescription = useMemo(() => {
    if (!myObservation) {
      return '';
    }

    const selectedGroup = xGroups[selectedIndex];
    if (!selectedGroup) {
      return '';
    }

    const rule = getRuleOutputExplanationForKeyFactors({
      explanations,
      originalLevelIndex,
      trait,
      metric,
      myObservation,
      selectedGroupLabel: selectedGroup.label,
      selectedGroupLevelValues: selectedGroup.groupLevels.map((gl) => gl.value),
    });
    return (
      <>
        <span className="text-gray-900">{rule.overview}</span>
      </>
    );
  }, [
    explanations,
    metric,
    myObservation,
    originalLevelIndex,
    selectedIndex,
    trait,
    xGroups,
  ]);

  return (
    <Card paddingClassName="flex flex-col px-10 py-8 gap-6 divide-y divide-gray-150">
      {chartDescription && (
        <div className="pl-5 border-l-2 border-royal-700 tex-sm tracking-tight">
          {chartDescription}
        </div>
      )}
      <Chart
        setSelectedIndex={(newIndex) => {
          navigateToTraitLevel(trait, newIndex);
        }}
        xGroups={xGroups}
        xLevels={xLevels}
      />
    </Card>
  );
};

const KeyFactorSection = ({
  metricColumnHeaderValue,
  controlVariableColumnHeaderValue,
  traits,
  trait,
  explanations,
  navigateToTraitLevel,
}: {
  metricColumnHeaderValue: string;
  controlVariableColumnHeaderValue: string;
  navigateToTraitLevel: (
    trait: Trait | null,
    levelIndex: number | null,
  ) => void;
  traits: Trait[];
  trait: Trait;
  explanations?: ExplanationsOutputDictionary;
}) => {
  const language = useLanguage();

  const { myObservation } = useObservation();

  const controlVariables = getControlVariableDefinitionsMapping({
    myObservation,
  });

  const controlVariable = useMemo(() => {
    return controlVariables[controlVariableColumnHeaderValue];
  }, [controlVariables, controlVariableColumnHeaderValue]);

  const metric = useMemo(() => {
    for (const focusArea of myObservation?.focusAreas ?? []) {
      for (const metric of focusArea.metrics) {
        if (metric.columnHeaderValue === metricColumnHeaderValue) {
          return metric;
        }
      }
    }
  }, [metricColumnHeaderValue, myObservation]);

  const handleGoBack = useCallback(() => {
    navigateToTraitLevel(null, null);
  }, [navigateToTraitLevel]);

  const currentTraitIndex = useMemo(() => {
    return traits.findIndex(
      (trait) =>
        trait.controlVariableColumnHeaderValue ===
        controlVariableColumnHeaderValue,
    );
  }, [traits, controlVariableColumnHeaderValue]);

  const selectNextTrait = useCallback(() => {
    const nextTrait = traits[currentTraitIndex + 1];

    if (nextTrait) {
      navigateToTraitLevel(nextTrait, null);
    }
  }, [traits, currentTraitIndex, navigateToTraitLevel]);

  const selectPreviousTrait = useCallback(() => {
    const previousTrait = traits[currentTraitIndex - 1];

    if (previousTrait) {
      navigateToTraitLevel(previousTrait, null);
    }
  }, [traits, currentTraitIndex, navigateToTraitLevel]);

  const targetRange = useMemo(() => {
    if (!myObservation || !traits[currentTraitIndex]) {
      return '';
    }
    return getTargetRangeDisplayForKeyFactors({
      trait: traits[currentTraitIndex],
      myObservation,
    });
  }, [myObservation, currentTraitIndex, traits]);

  const exampleExplanation = useMemo(() => {
    const exampleExplanation = getExampleExplanationForKeyFactors({
      trait,
      explanations,
    });
    return exampleExplanation.overview;
  }, [explanations, trait]);

  const breakdownExplanation = useMemo(() => {
    if (!myObservation) {
      return '';
    }

    const breakdownExplanation = getBreakdownExplanationForKeyFactors({
      trait,
      explanations,
      myObservation,
      language,
      metric: metric?.columnHeaderValue ?? '',
      targetRange: targetRange ?? '',
    });
    return breakdownExplanation.overview;
  }, [
    explanations,
    language,
    metric?.columnHeaderValue,
    myObservation,
    targetRange,
    trait,
  ]);

  const optimalRule = useMemo(() => {
    if (!myObservation) {
      return '';
    }

    const optimalRuleExplanation = getOptimalRuleExplanationForKeyFactors({
      trait,
      explanations,
      metric: metric?.columnHeaderValue ?? '',
      language,
      myObservation,
      targetRange: targetRange ?? '',
    });
    return optimalRuleExplanation.overview;
  }, [
    myObservation,
    trait,
    explanations,
    metric?.columnHeaderValue,
    language,
    targetRange,
  ]);

  const targetRangeProps = useMemo(() => {
    return getTargetRangeProps({
      controlVariableDefinitions: Object.values(controlVariables),
      trait,
    });
  }, [controlVariables, trait]);

  return (
    <Card paddingClassName="flex flex-col flex-none px-10 py-8 divide-y divide-gray-150 w-full @5xl:w-[408px] overflow-y-auto">
      <div className="flex justify-between pb-6">
        <TouchTarget
          className={'inline-block w-fit'}
          content={() => (
            <span className="text-sm text-royal-800 font-bold tracking-tight">
              &lt; {controlVariable?.displayName}
            </span>
          )}
          behavior={{
            type: 'onClick',
            onClick: handleGoBack,
          }}
        />
        <span className="text-sm text-royal-800 font-bold tracking-tight">
          <TouchTarget
            className={'inline-block w-fit'}
            content={() => (
              <span
                className={cn(
                  'text-sm text-royal-800 font-bold tracking-tight mr-2',
                  { invisible: currentTraitIndex === 0 },
                )}
              >
                &lt;
              </span>
            )}
            behavior={{
              type: 'onClick',
              onClick: selectPreviousTrait,
            }}
          />
          {currentTraitIndex + 1} of {traits.length}
          <TouchTarget
            className={'inline-block w-fit'}
            content={() => (
              <span
                className={cn(
                  'text-sm text-royal-800 font-bold tracking-tight ml-2',
                  {
                    invisible: currentTraitIndex === traits.length - 1,
                  },
                )}
              >
                &gt;
              </span>
            )}
            behavior={{
              type: 'onClick',
              onClick: selectNextTrait,
            }}
          />
        </span>
      </div>
      <div className="py-6 flex flex-col gap-2">
        {controlVariable && metric && (
          <S2Typography
            variant={'body-6-bold'}
            textColorVariant={'gray-800'}
            translatedContent={language.keyFactorsBreakdownTraitCardLikelihood({
              metric,
              controlVariable,
            })}
          />
        )}
        <ExpandableExplanation
          overview={breakdownExplanation}
          details={exampleExplanation}
          disableBorder={true}
          expandButtonLabels={{
            show: language.keyFactorsBreakdownShowFullAnalysis(),
            hide: language.keyFactorsBreakdownHideFullAnalysis(),
          }}
        />
      </div>
      <div className="flex flex-col pt-6">
        <div className="py-2 text-gray-800 text-xs leading-5 tracking-widest flex items-center ">
          <S2Typography
            variant={'body-7'}
            as="h4"
            textColorVariant="gray-800"
            dangerouslyOverrideClassNameFn={(cn) => `${cn} uppercase`}
            translatedContent={language.targetRange()}
          />
          <span> {targetRange}</span>
        </div>
        {targetRangeProps && <TargetRangeGraph {...targetRangeProps} />}

        {optimalRule && (
          <div className="rounded-lg p-4 flex gap-2.5 bg-royal-100 mt-4 items-center">
            <span className="grow">{optimalRule}</span>

            <AIMagicIconSvg
              svg={{
                className: 'text-royal-700 align-self-center',
                width: 100,
              }}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export interface BreakdownSectionProps
  extends BreakdownSectionSerializableProps {
  setSelectedTrait: (trait: Trait | null) => void;
}

export const BreakdownSection = ({
  metricColumnHeaderValue,
  controlVariableColumnHeaderValue,
  setSelectedTrait,
  trait,
  traits,
  explanations,
}: BreakdownSectionProps) => {
  const { myObservation } = useObservation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const datumsAndBestIndex = useCallback(
    (datumsTrait: Trait | null) => {
      if (!myObservation) {
        return null;
      }
      const mapping =
        getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation({
          myObservation,
        });

      if (datumsTrait === null) {
        return null;
      }

      return traitToTraitLevelDatumsForKeyFactors({
        activeTrait: datumsTrait,
        controlVariableDefinitionsMapping: mapping,
      });
    },
    [myObservation],
  );

  // Preselect the best trait datum if none is selected yet.
  useEffect(() => {
    if (selectedIndex === null && trait !== null) {
      const d = datumsAndBestIndex(trait);

      if (d !== null && d.bestTraitDatumIndex !== null) {
        setSelectedIndex(d.bestTraitDatumIndex);
      }
    }
  }, [datumsAndBestIndex, selectedIndex, trait]);

  const navigateToTraitLevel = useCallback(
    (newTrait: Trait | null, levelIndex: number | null) => {
      if (newTrait === null) {
        setSelectedTrait(null);
        setSelectedIndex(0);
      }

      setSelectedTrait(newTrait);

      const d = datumsAndBestIndex(newTrait);

      if (levelIndex !== null) {
        setSelectedIndex(levelIndex);
      } else if (d !== null && d.bestTraitDatumIndex !== null) {
        setSelectedIndex(d.bestTraitDatumIndex);
      } else {
        setSelectedIndex(0);
      }
    },
    [datumsAndBestIndex, setSelectedTrait],
  );

  const originalLevelIndex = useMemo(() => {
    const d = datumsAndBestIndex(trait);

    if (
      d === null ||
      selectedIndex === null ||
      selectedIndex < 0 ||
      selectedIndex >= d.datums.length
    ) {
      return null;
    }

    const datum = d.datums[selectedIndex];
    return datum.originalIndex;
  }, [datumsAndBestIndex, selectedIndex, trait]);

  return (
    <div className="flex @5xl:flex-row flex-col-reverse gap-6 overflow-x-auto">
      <KeyFactorSection
        metricColumnHeaderValue={metricColumnHeaderValue}
        controlVariableColumnHeaderValue={controlVariableColumnHeaderValue}
        navigateToTraitLevel={navigateToTraitLevel}
        traits={traits}
        trait={trait}
        explanations={explanations}
      />
      {selectedIndex !== null && (
        <ChartSection
          trait={trait}
          metricColumnHeaderValue={metricColumnHeaderValue}
          explanations={explanations}
          navigateToTraitLevel={navigateToTraitLevel}
          selectedIndex={selectedIndex}
          originalLevelIndex={originalLevelIndex}
        />
      )}
    </div>
  );
};
